import { useSyncExternalStore } from 'react';

const PING_TIMEOUT_MS = 5000;
const PING_INTERVAL_MS = 30000;

/**
 * Verify actual connectivity with a lightweight HEAD request.
 * Falls back to true if the check itself errors in an unexpected way.
 */
const checkConnectivity = async (): Promise<boolean> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
  try {
    // HEAD request to current origin — tiny payload, no CORS issues
    await fetch(window.location.origin, {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-store',
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

// ---------------------------------------------------------------------------
// Shared singleton: one set of event listeners + one interval for all callers
// ---------------------------------------------------------------------------

let isOnline = navigator.onLine;
const subscribers = new Set<() => void>();
let started = false;
let intervalId: ReturnType<typeof setInterval> | undefined;
let verifyCounter = 0;

function notify() {
  for (const cb of subscribers) cb();
}

async function verify() {
  const callId = ++verifyCounter;
  const actual = await checkConnectivity();
  if (callId === verifyCounter && actual !== isOnline) {
    isOnline = actual;
    notify();
  }
}

function handleOnline() {
  isOnline = true;
  notify();
  verify();
}

function handleOffline() {
  isOnline = navigator.onLine;
  notify();
  verify();
}

function startListening() {
  if (started) return;
  started = true;
  verify();
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  intervalId = setInterval(verify, PING_INTERVAL_MS);
}

function stopListening() {
  if (!started) return;
  started = false;
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
  clearInterval(intervalId);
}

function subscribe(callback: () => void) {
  subscribers.add(callback);
  if (subscribers.size === 1) startListening();
  return () => {
    subscribers.delete(callback);
    if (subscribers.size === 0) stopListening();
  };
}

function getSnapshot() {
  return isOnline;
}

/**
 * Hook that tracks the browser's online/offline status.
 * Uses a shared singleton so multiple callers share one set of
 * event listeners and one periodic connectivity check.
 */
export const useIsOnline = (): boolean => {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
